import { useState, useEffect, useCallback } from "react";
import { Stack, Pagination } from "@mui/material";
import Div from "../../../components/Div";
import Input from "../../../components/Input";
import PageTitle from "../../../components/PageTitle";
import Form from "../../../components/Form";
import FormButton from "../../../components/FormButton";
import { API_URL } from "../../../config";
import { getToken } from "../../../util/validation";

export default function GetInteractionCalling() {
    const [dataList, setDataList] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const token = JSON.parse(getToken);

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedProducts, setSelectedProducts] = useState([]);

    // History States
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [interactionHistory, setInteractionHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const productOptions = [
        "Renewal Discussion", "Renewal Expansion", "New Domain", "Jumpcloud", 
        "Scalefusion", "Checkpoint", "Gemini Ultra", "Gemini Enterprise", 
        "Okta", "Jamf", "Wizz", "AWS", "GCP", "MiniOrange"
    ];

    const getCurrentMonth = () => {
        const date = new Date();
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    };
    const [month, setMonth] = useState(getCurrentMonth());

    // Fetch Main Dashboard Data
    const fetchData = useCallback(async () => {
        try {
            const response = await fetch(API_URL + 'adminpanel/get_interaction_calling_data/', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': token.login_token 
                },
                body: JSON.stringify({ month }),
            });
            const result = await response.json();
            if (result.data) setDataList(result.data);
        } catch (error) {
            console.error("Fetch error", error);
        }
    }, [month, token.login_token]);

    // Fetch Interaction History for a specific domain
    const fetchHistory = async (domain) => {
        setLoadingHistory(true);
        try {
            const response = await fetch(API_URL + 'adminpanel/get_interaction_history/', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': token.login_token 
                },
                body: JSON.stringify({ domain_name: domain }),
            });
            const result = await response.json();
            setInteractionHistory(result.history || []);
            setShowHistoryModal(true);
        } catch (error) {
            console.error("History error", error);
            alert("Failed to load history.");
        }
        setLoadingHistory(false);
    };

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleCheckboxChange = (event) => {
        const { value, checked } = event.target;
        if (checked) {
            setSelectedProducts((prev) => [...prev, value]);
        } else {
            setSelectedProducts((prev) => prev.filter((item) => item !== value));
        }
    };

    async function handleSaveInteraction(event) {
        event.preventDefault();
        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData.entries());
        
        const payload = {
            renewal_id: selectedCustomer.renewal_id,
            domain_name: selectedCustomer.domain_name,
            products: selectedProducts.join(", "),
            feedback: data.feedback,
            calling_date: data.calling_date 
        };

        try {
            const response = await fetch(API_URL + 'adminpanel/insert_interaction_log/', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': token.login_token 
                },
                body: JSON.stringify({
                    email: token.email,
                    formData: payload
                }),
            });

            if (response.ok) {
                alert("Interaction Log Saved Successfully!");
                setShowModal(false);
                setSelectedProducts([]);
                fetchData(); 
            } else {
                alert("Error: Failed to save to database");
            }
        } catch (error) {
            console.error("Save error:", error);
        }
    }

    // Filtering Logic
    const filteredList = dataList.filter(item => 
        item.domain_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredList.length / itemsPerPage);

    return (
        <>
            {/* --- 1. INSERT INTERACTION MODAL --- */}
            {showModal && (
                <Div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <dialog open style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', width: '95%', maxWidth: '500px', border: 'none', boxShadow: '0 5px 15px rgba(0,0,0,0.3)' }}>
                        <h5 className="mb-3">Interaction Log: {selectedCustomer?.domain_name}</h5>
                        <hr />
                        <Form onSubmit={handleSaveInteraction}>
                            <Div className="form-group row mb-3">
                                <label className="col-sm-4 col-form-label fw-bold">Products</label>
                                <Div className="col-sm-8" style={{ border: '1px solid #ced4da', borderRadius: '4px', padding: '10px', maxHeight: '150px', overflowY: 'auto' }}>
                                    {productOptions.map((prod) => (
                                        <div key={prod} className="form-check mb-1">
                                            <input type="checkbox" className="form-check-input" value={prod} id={`chk-${prod}`} onChange={handleCheckboxChange} />
                                            <label className="form-check-label ml-2" htmlFor={`chk-${prod}`} style={{ cursor: 'pointer' }}>{prod}</label>
                                        </div>
                                    ))}
                                </Div>
                            </Div>

                            <Div className="form-group row mb-3">
                                <label className="col-sm-4 col-form-label fw-bold">Calling Feedback</label>
                                <Div className="col-sm-8">
                                    <textarea name="feedback" className="form-control" rows="3" placeholder="Enter interaction notes..." style={{ borderRadius: '4px' }} required></textarea>
                                </Div>
                            </Div>

                            <Div className="form-group row mb-3">
                                <label className="col-sm-4 col-form-label fw-bold">Calling Date</label>
                                <Div className="col-sm-8">
                                    <input type="date" name="calling_date" className="form-control" style={{ borderRadius: '4px' }} />
                                </Div>
                            </Div>

                            <Div className="d-flex justify-content-end mt-4">
                                <FormButton type="submit" className="btn btn-primary mr-2">Save Interaction</FormButton>
                                <FormButton type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</FormButton>
                            </Div>
                        </Form>
                    </dialog>
                </Div>
            )}

            {/* --- 2. VIEW HISTORY MODAL --- */}
            {showHistoryModal && (
                <Div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <dialog open style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', width: '95%', maxWidth: '850px', border: 'none', boxShadow: '0 5px 15px rgba(0,0,0,0.5)' }}>
                        <h5 className="mb-3">Interaction History: {interactionHistory[0]?.domain_name || "Record"}</h5>
                        <hr />
                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table table-sm table-bordered table-striped">
                                <thead className="thead-dark">
                                    <tr>
                                        <th>Date & Time</th>
                                        <th>Products Discussed</th>
                                        <th>Feedback/Notes</th>
                                        <th>Next Call Date</th>
                                        <th>Updated By</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {interactionHistory.length > 0 ? interactionHistory.map((h, i) => (
                                        <tr key={i}>
                                            <td style={{ fontSize: '12px' }}>{new Date(h.timestamp).toLocaleString()}</td>
                                            <td style={{ fontSize: '12px' }}>{h.products || '--'}</td>
                                            <td style={{ fontSize: '12px' }}>{h.feedback}</td>
                                            <td style={{ fontSize: '12px' }}>{h.calling_date || 'N/A'}</td>
                                            <td style={{ fontSize: '12px' }}>{h.updated_by}</td>
                                        </tr>
                                    )) : <tr><td colSpan="5" className="text-center">No history found for this domain.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        <Div className="d-flex justify-content-end mt-3 pt-2" style={{ borderTop: '1px solid #eee' }}>
                            <FormButton className="btn btn-secondary btn-sm" onClick={() => setShowHistoryModal(false)}>Close History</FormButton>
                        </Div>
                    </dialog>
                </Div>
            )}

            <PageTitle title="Interaction Calling Dashboard" />

            <Div className="col-md-12 mb-4 d-flex justify-content-between align-items-center">
                <Div className="d-flex gap-3">
                    <Input type='month' value={month} onChange={(e) => setMonth(e.target.value)} />
                    <div style={{ maxWidth: '400px', width: '300px' }}>
                        <input type="text" className="form-control" placeholder="Search Customer Domain..." value={searchTerm} style={{ border: '1px solid black' }} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
                    </div>
                </Div>
            </Div>

            <Div className="col-md-12">
                <table className="table table-bordered table-striped">
                    <thead>
                        <tr className="bg-light">
                            <th>Customer Domain</th>
                            <th>Zone</th>
                            <th>Google Renewal Date</th>
                            <th>Shivaami Renewal Date</th>
                            <th>Price</th>
                            <th>Segments</th>
                            <th>Assigned to</th>
                            <th>Action</th>
                            <th>View Data</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.map((item) => (
                            <tr key={item.renewal_id}>
                                <td>{item.domain_name}</td>
                                <td>{item.zone || '--'}</td>
                                <td>{item.google_renewal_data || '--'}</td>
                                <td>{item.shivaami_renewal_date}</td>
                                <td>₹{item.price}</td> 
                                <td>{item.segments}</td>
                                <td>{item.assigned_to}</td>
                                <td>
                                    <button className="btn btn-primary btn-xs" onClick={() => { setSelectedCustomer(item); setShowModal(true); }}>
                                        Interaction Calling
                                    </button>
                                </td>
                                <td>
                                    <button className="btn btn-outline-info btn-xs" onClick={() => fetchHistory(item.domain_name)}>
                                        {loadingHistory ? "..." : "📊 View History"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {totalPages > 1 && (
                    <Stack spacing={2} className="mt-3">
                        <Pagination count={totalPages} page={currentPage} onChange={(e, v) => setCurrentPage(v)} color="primary" />
                    </Stack>
                )}
            </Div>
        </>
    );
}